/* eslint-disable react/prop-types */
import { Drawer, IconButton, Tab, Tabs } from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import dayjs from 'dayjs'
import React from 'react'

import { apiEndpoints } from 'api/common'
import { makeRequestUrl } from 'app/helpers'
import { get } from 'request'

import * as styles from './styles'

/**
 * Per-student history drawer.
 *
 * Opens when an admin clicks a child on the Family card. Two tabs:
 *   - Enrolments  → all enrolments for this student (with subscription
 *                   status — the FSM-on-Enrolment refactor put what used
 *                   to be the "subscription" lifecycle directly on the
 *                   enrolment row, so this tab covers both views).
 *   - Attendance  → attendance rows for this student.
 *
 * Backend endpoints used (all verified 200 on local):
 *   GET /oh_classes/enrolment?student=<uuid>
 *   GET /oh_classes/attendance/?student=<uuid>
 */
const TAB_DEFS = [
  { key: 'enrolments', label: 'Enrolments' },
  { key: 'attendance', label: 'Attendance' }
]

const fetchByTab = async ({ tab, studentUuid }) => {
  if (tab === 'enrolments') {
    const url = makeRequestUrl(apiEndpoints.fetchEnrolment, {
      student: studentUuid,
      limit: 100
    })
    const res = await get(url)
    return res.data?.results || []
  }
  if (tab === 'attendance') {
    const url = makeRequestUrl(apiEndpoints.attendance, {
      student: studentUuid,
      limit: 100
    })
    const res = await get(url)
    return res.data?.results || []
  }
  return []
}

const EnrolmentItem = ({ item }) => {
  const courseName = item?.batch?.course?.name || item?.batch?.name || 'Unknown course'
  const status = (item?.status || '').toLowerCase()
  const subStatus = item?.current_subscription_status
  return (
    <styles.Item>
      <styles.ItemTitle>
        <span>{courseName}</span>
        <styles.StatusPill $status={status}>{status || '—'}</styles.StatusPill>
      </styles.ItemTitle>
      <styles.ItemMeta>
        <span>{item?.type || '—'}</span>
        <span>·</span>
        <span>
          {item?.start_date ? dayjs(item.start_date).format('D MMM YY') : '—'}
          {item?.end_date ? ` → ${dayjs(item.end_date).format('D MMM YY')}` : ''}
        </span>
        {subStatus ? (
          <>
            <span>·</span>
            <span>sub: {subStatus.toLowerCase().replace(/_/g, ' ')}</span>
          </>
        ) : null}
      </styles.ItemMeta>
    </styles.Item>
  )
}

const AttendanceItem = ({ item }) => {
  const date = item?.schedule?.date
  const attended = item?.attended
  return (
    <styles.Item>
      <styles.ItemTitle>
        <span>{date ? dayjs(date).format('ddd D MMM YY') : '—'}</span>
        <styles.StatusPill $status={attended ? 'active' : 'closed'}>
          {attended ? 'attended' : 'absent'}
        </styles.StatusPill>
      </styles.ItemTitle>
      <styles.ItemMeta>
        <span>
          {item?.schedule?.start_time
            ? `${item.schedule.start_time}–${item.schedule.end_time}`
            : '—'}
        </span>
        <span>·</span>
        <span>{item?.enrolment?.batch?.course?.name || '—'}</span>
      </styles.ItemMeta>
    </styles.Item>
  )
}

export const StudentHistoryDrawer = ({ open, onClose, student }) => {
  const [tab, setTab] = React.useState('enrolments')
  const [byTab, setByTab] = React.useState({})
  const [loadingTab, setLoadingTab] = React.useState(null)

  const studentUuid = student?.user_id || student?.user?.uuid

  React.useEffect(() => {
    if (!open || !studentUuid || byTab[tab]) return
    let cancelled = false
    setLoadingTab(tab)
    fetchByTab({ tab, studentUuid })
      .then((results) => {
        if (cancelled) return
        setByTab((prev) => ({ ...prev, [tab]: results }))
      })
      .catch((err) => {
        if (cancelled) return
        console.error('StudentHistoryDrawer fetch failed', tab, err)
        setByTab((prev) => ({ ...prev, [tab]: [] }))
      })
      .finally(() => {
        if (!cancelled) setLoadingTab(null)
      })
    return () => {
      cancelled = true
    }
  }, [open, tab, studentUuid, byTab])

  // Reset cached data each time a new student is opened.
  React.useEffect(() => {
    setByTab({})
    setTab('enrolments')
  }, [studentUuid])

  const items = byTab[tab] || []
  const isLoading = loadingTab === tab && !byTab[tab]

  const studentName =
    student?.user?.full_name
    || `${student?.user?.first_name || ''} ${student?.user?.last_name || ''}`.trim()
    || '—'

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <styles.DrawerInner>
        <styles.Header>
          <div>
            <styles.HeaderTitle>{studentName}</styles.HeaderTitle>
            <styles.HeaderSub>
              {student?.standard?.name || 'no standard'}
            </styles.HeaderSub>
          </div>
          <IconButton size="small" onClick={onClose} aria-label="close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </styles.Header>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          {TAB_DEFS.map((t) => (
            <Tab key={t.key} label={t.label} value={t.key} />
          ))}
        </Tabs>
        <styles.Body>
          {isLoading ? (
            <styles.Loading>loading…</styles.Loading>
          ) : items.length === 0 ? (
            <styles.Empty>no {tab} to show</styles.Empty>
          ) : tab === 'enrolments' ? (
            items.map((it) => <EnrolmentItem key={it.uuid} item={it} />)
          ) : (
            items.map((it) => <AttendanceItem key={it.uuid} item={it} />)
          )}
        </styles.Body>
      </styles.DrawerInner>
    </Drawer>
  )
}
